import { Box, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";

import theme from "../../theme";
import DepartmentMaster from "./DepartmentMaster";
import { ATSProvider } from "../atsModule/context/ATSContext";
import DepartmentsList from "../atsModule/pages/DepartmentsList";

// The Daily Tick sidebar's "Departments" entry.
//
// There are two kinds of department now (see src/utils/departmentTypes.js) and
// until this screen existed only one of them was reachable from here: the org
// chart lived behind /ats, so getting to it meant switching module, which is a
// long way to go for a list that belongs next to this one.
//
// So both are here, as tabs over the two existing screens rather than a third
// implementation of either:
//
//   Ticket departments  DepartmentMaster, unchanged. Creates and edits the rows
//                       the ticket screens' dropdown reads, and now also chooses
//                       whether a department is on the org chart too.
//   Organization        the ATS module's own DepartmentsList, mounted in this
//                       shell. Cards, counts, and drill-down into the chart.
//
// The tab lives in the URL rather than in state so that coming back from a
// department's chart lands on the tab it was opened from, instead of silently
// resetting to the first one.

const TABS = [
  {
    value: "tickets",
    label: "Ticket departments",
    icon: <ConfirmationNumberRoundedIcon sx={{ fontSize: 18 }} />,
  },
  {
    value: "organization",
    label: "Organization",
    icon: <AccountTreeRoundedIcon sx={{ fontSize: 18 }} />,
  },
];

const DepartmentsHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const active = TABS.some((tab) => tab.value === requested)
    ? requested
    : "tickets";

  const selectTab = (_event, value) => {
    // `replace` so the two tabs do not stack up in history: pressing back from
    // here should leave the screen, not walk back through the tabs.
    setSearchParams(value === "tickets" ? {} : { tab: value }, {
      replace: true,
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: `1px solid ${theme.lightTheme.placeholder}`,
          bgcolor: "white",
          px: { xs: 1, sm: 2 },
        }}
      >
        <Tabs
          value={active}
          onChange={selectTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              gap: 0.75,
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Both tabs stay mounted only while selected. DepartmentsList fetches on
          mount, so keeping the hidden one alive would mean a request for a list
          nobody is looking at every time this screen opens. */}
      {active === "tickets" ? (
        <DepartmentMaster />
      ) : (
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {/* DepartmentsList reads store_id and user_id from the ATS context,
              which only needs the login already in AutoTaskAuthContext — so it
              works here without the rest of the ATS shell. */}
          <ATSProvider>
            <DepartmentsList />
          </ATSProvider>
        </Box>
      )}
    </Box>
  );
};

export default DepartmentsHub;
